import React from 'react';
import { CircularProgress, useTheme, alpha } from '@mui/material';
import Button, { ButtonProps } from '@mui/material/Button';
import { Link as RouterLink } from 'react-router-dom';

import { getColorByStatus, StatusSeverity } from './colorUtilsV2';

export interface ButtonV2Props extends Omit<ButtonProps, 'startIcon' | 'endIcon' | 'color'> {
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  children: React.ReactNode;
  statusColor?: StatusSeverity;
  to?: string;
  loading?: boolean;
  size?: 'small' | 'medium' | 'large';
  opacity?: number;
}

/**
 * ButtonV2 is a wrapper for MUI Button with icon, iconPosition, navigation, and custom styling support.
 */
export const ButtonV2 = ({
  icon,
  iconPosition = 'start',
  children,
  variant = 'contained',
  statusColor,
  onClick,
  to,
  loading = false,
  size = 'medium',
  opacity = 0.7,
  ...rest
}: ButtonV2Props) => {
  const theme = useTheme();
  const baseColor = getColorByStatus(statusColor ?? 'primary', theme);

  // Size-specific styling
  const sizeStyles = {
    small: { py: 0.75, px: 2.5, fontSize: 14, iconSize: 18 },
    medium: { py: 1, px: 4, fontSize: 16, iconSize: 20 },
    large: { py: 1.5, px: 6, fontSize: 18, iconSize: 24 }
  };
  const currentSizeStyle = sizeStyles[size];

  // Check if button is disabled
  const isDisabled = loading || rest.disabled;

  // If icon is present, clone it with the correct color and size
  const coloredIcon = icon
    ? React.cloneElement(icon as React.ReactElement, {
        sx: {
          color: baseColor,
          fontSize: currentSizeStyle.iconSize,
          ...((icon as React.ReactElement).props?.sx || {}),
          opacity: isDisabled ? 0.4 : undefined
        }
      })
    : undefined;

  // Determine icon props based on position
  const iconProps = iconPosition === 'start' ? { startIcon: coloredIcon } : { endIcon: coloredIcon };
  const buttonContent = loading ? <CircularProgress size={currentSizeStyle.iconSize} color="inherit" /> : children;
  const component = to ? RouterLink : undefined;
  const componentProps = to ? { to } : {};

  // Get the color for the status and apply opacity as needed
  const backgroundColor =
    variant === 'contained'
      ? statusColor
        ? alpha(baseColor, opacity)
        : alpha(theme.palette.text.primary, 0.2)
      : 'transparent';
  const hoverBackgroundColor =
    variant === 'contained'
      ? statusColor
        ? alpha(baseColor, Math.min(opacity + 0.2, 1))
        : alpha(theme.palette.text.primary, 0.3)
      : alpha(baseColor, 0.1);
  const textColor = variant === 'outlined' || variant === 'text' ? baseColor : theme.palette.text.primary;
  const borderColor = variant === 'outlined' ? baseColor : 'transparent';

  return (
    <Button
      variant={variant}
      onClick={onClick}
      disabled={loading || rest.disabled}
      component={component}
      {...componentProps}
      {...iconProps}
      {...rest}
      sx={{
        ...rest.sx,
        backgroundColor,
        color: textColor,
        textTransform: 'capitalize',
        borderRadius: 1.5,
        py: currentSizeStyle.py,
        px: currentSizeStyle.px,
        fontSize: currentSizeStyle.fontSize,
        border: variant === 'outlined' ? `1px solid ${borderColor}` : undefined,
        boxShadow: 'none',
        '&:hover': {
          backgroundColor: hoverBackgroundColor,
          borderColor: variant === 'outlined' ? baseColor : undefined
        }
      }}
    >
      {buttonContent}
    </Button>
  );
};
